import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getRun, applyRun } from '../api/lens'
import { LENS_URL } from '../api/client'
import type { LensRun, LensHypothesis } from '../types'
import {
  ChevronLeft, CheckCircle, AlertCircle, Loader2,
  Download, Terminal, Circle, Ban, Search,
} from 'lucide-react'
import { Button } from '../components/ui/button'

// ── Pipeline stage definitions ────────────────────────────────────────────────

const PIPELINE_STAGES = [
  { key: 'collection', label: 'Collection',  desc: 'Downloading records' },
  { key: 'analysis',   label: 'Exploration', desc: 'Analysing patterns'  },
  { key: 'cleaning',   label: 'Cleaning',    desc: 'Fixing & validating' },
  { key: 'training',   label: 'Training',    desc: 'Fitting the model'   },
  { key: 'results',    label: 'Results',     desc: 'Write-back available' },
] as const

type StageKey    = (typeof PIPELINE_STAGES)[number]['key']
type StageStatus = 'pending' | 'active' | 'done' | 'error' | 'available' | 'applied' | 'skipped'

const WRITEBACK_GOALS = new Set(['classification', 'segmentation', 'anomaly'])

// ── Hypothesis display ────────────────────────────────────────────────────────

const HYP_LABELS: Record<string, string> = {
  correlation: 'Correlation', association: 'Association', null_impact: 'Null impact',
  distribution: 'Distribution', cardinality: 'Cardinality', null_rate: 'Null rate',
}
const STRENGTH_COLOR: Record<string, string> = {
  strong: 'text-emerald-600', moderate: 'text-amber-600', weak: 'text-ink-dim',
  high: 'text-red-500', medium: 'text-amber-500', low: 'text-ink-dim',
}

// ── Main component ────────────────────────────────────────────────────────────

export function LensRunDetail() {
  const { id } = useParams<{ id: string }>()
  const [run, setRun]         = useState<LensRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [logs, setLogs]       = useState<string[]>([])
  const [logsDone, setLogsDone] = useState(false)
  const [stages, setStages]   = useState<Record<StageKey, StageStatus>>({
    collection: 'pending', analysis: 'pending', cleaning: 'pending',
    training: 'pending', results: 'pending',
  })
  const logsEndRef = useRef<HTMLDivElement>(null)

  // Load run on mount
  useEffect(() => {
    if (!id) return
    getRun(id).then(setRun).finally(() => setLoading(false))
  }, [id])

  // Poll while running (to refresh result after completion)
  useEffect(() => {
    if (!run || run.status === 'done' || run.status === 'failed') return
    const t = setInterval(() => {
      getRun(run.id).then((r) => {
        setRun(r)
        if (r.status === 'done' || r.status === 'failed') clearInterval(t)
      })
    }, 2000)
    return () => clearInterval(t)
  }, [run?.status])

  // Sync Results stage when run.applied_at changes (after Apply button)
  useEffect(() => {
    if (!run) return
    if (run.status === 'done') {
      const goalType = (run.result as any)?.goal_type as string | undefined
      setStages((prev) => ({
        ...prev,
        results: run.applied_at
          ? 'applied'
          : WRITEBACK_GOALS.has(goalType ?? '') ? 'available' : 'skipped',
      }))
    }
  }, [run?.applied_at, run?.status, (run?.result as any)?.goal_type])

  // SSE log + stage streaming
  useEffect(() => {
    if (!id || !run) return

    // Replay persisted entries for completed runs
    if (run.status === 'done' || run.status === 'failed') {
      if (run.logs && run.logs.length > 0) {
        const textLines: string[] = []
        const stageMap: Partial<Record<StageKey, StageStatus>> = {}
        for (const entry of run.logs) {
          if (typeof entry === 'string') {
            textLines.push(entry)
          } else if (entry.log) {
            textLines.push(entry.log)
          } else if (entry.stage && entry.status) {
            stageMap[entry.stage as StageKey] = entry.status as StageStatus
          }
        }
        setLogs(textLines)
        setStages((prev) => ({ ...prev, ...stageMap }))
        setLogsDone(true)
      }
      return
    }

    // Stream live entries via SSE
    const token = localStorage.getItem('accessToken') ?? ''
    const url   = `${LENS_URL}/api/lens/runs/${id}/logs/stream`
    let controller: AbortController | null = new AbortController()

    async function streamLogs() {
      try {
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller!.signal,
        })
        if (!resp.body) return
        const reader  = resp.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const parts = buf.split('\n\n')
          buf = parts.pop() ?? ''
          for (const part of parts) {
            const line = part.replace(/^data: /, '').trim()
            if (!line) continue
            try {
              const evt = JSON.parse(line)
              if (evt.log) {
                setLogs((prev) => [...prev, evt.log as string])
              } else if (evt.stage && evt.status) {
                setStages((prev) => ({ ...prev, [evt.stage]: evt.status as StageStatus }))
              }
              if (evt.done) {
                setLogsDone(true)
                getRun(id!).then(setRun)
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch { /* aborted or network error */ }
    }

    streamLogs()
    return () => { controller?.abort(); controller = null }
  }, [id, run?.status])

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const handleApply = async () => {
    if (!run) return
    setApplying(true)
    try { setRun(await applyRun(run.id)) } finally { setApplying(false) }
  }

  if (loading) return <p className="text-sm text-ink-dim">Loading…</p>
  if (!run)    return <p className="text-sm text-red-500">Run not found.</p>

  const result   = run.result as Record<string, any> | null
  const goalType = result?.goal_type as string | undefined
  const isActive = run.status === 'queued' || run.status === 'running'

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/lens/scenarios/${run.scenario_id}`} className="text-ink-dim hover:text-ink-dim">
          <ChevronLeft size={18} />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-ink">Run result</h1>
          <p className="text-theme-xs text-ink-dim">
            {run.completed_at ? new Date(run.completed_at).toLocaleString() : 'In progress…'}
          </p>
        </div>
        {run.status === 'done' && stages.results === 'available' && (
          <Button variant="primary" icon={Download} loading={applying} onClick={handleApply} disabled={applying}>
            {applying ? 'Applying…' : 'Apply to Rabbit'}
          </Button>
        )}
        {stages.results === 'applied' && (
          <div className="flex items-center gap-1.5 text-theme-xs text-emerald-600 font-medium">
            <CheckCircle size={14} /> Applied {new Date(run.applied_at!).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Error banner */}
      {run.status === 'failed' && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm text-red-700">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{run.error ?? 'Run failed'}</span>
        </div>
      )}

      {/* ── Pipeline stage stepper ──────────────────────────────────────────── */}
      <PipelineStages stages={stages} />

      <div className="grid grid-cols-[1fr_300px] gap-6 mt-6">
        {/* ── Main results ──────────────────────────────────────────────────── */}
        <div className="space-y-6">

          {/* Live log */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Terminal size={13} className="text-ink-dim" />
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">Run log</h2>
              {isActive && !logsDone && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
            </div>
            <div className="bg-slate-900 rounded-lg p-3 font-mono text-theme-xs text-ink-dim max-h-56 overflow-y-auto">
              {logs.length === 0 ? (
                <span className="text-ink-dim">{isActive ? 'Waiting for logs…' : 'No logs.'}</span>
              ) : (
                logs.map((line, i) => (
                  <div key={i} className="leading-relaxed">
                    <span className="text-ink-dim select-none mr-2">{String(i + 1).padStart(2, '0')}</span>
                    <span>{line}</span>
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </section>

          {/* Metrics */}
          {result?.metrics && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">Metrics</h2>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(result.metrics as Record<string, number>).map(([k, v]) => (
                  <div key={k} className="border border-paper-border rounded-lg p-3">
                    <p className="text-[10px] text-ink-dim uppercase tracking-wide mb-0.5">{k.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-semibold text-ink">
                      {typeof v === 'number' ? (v < 1 && v > 0 ? `${(v * 100).toFixed(1)}%` : v.toFixed(3)) : v}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Feature importance */}
          {result?.feature_importance && (result.feature_importance as any[]).length > 0 && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">Feature importance</h2>
              <div className="space-y-1.5">
                {(result.feature_importance as { field: string; score: number }[]).map((f) => (
                  <div key={f.field} className="flex items-center gap-3">
                    <span className="text-theme-xs text-ink w-40 truncate">{f.field}</span>
                    <div className="flex-1 h-2 bg-paper-sunken rounded-full overflow-hidden">
                      <div className="h-full bg-accent rounded-full" style={{ width: `${Math.round(f.score * 100)}%` }} />
                    </div>
                    <span className="text-theme-xs text-ink-dim w-10 text-right">{(f.score * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Goal-specific result tables */}
          {goalType === 'classification' && result?.scores && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">
                Record scores <span className="text-ink-dim font-normal">({(result.scores as any[]).length} records)</span>
              </h2>
              <ScoresTable scores={(result.scores as any[]).slice(0, 50)} />
            </section>
          )}
          {goalType === 'segmentation' && result?.segments && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">Segments</h2>
              <SegmentsTable segments={result.segments as any[]} featureNames={result.feature_names as string[]} />
            </section>
          )}
          {goalType === 'association' && result?.rules && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">
                Association rules <span className="text-ink-dim font-normal">({(result.rules as any[]).length} rules)</span>
              </h2>
              <RulesTable rules={(result.rules as any[]).slice(0, 30)} />
            </section>
          )}
          {goalType === 'forecast' && result?.forecast_type === 'multi_entity' && result?.predictions && (
            <section>
              <div className="flex items-baseline gap-2 mb-4">
                <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">Per-entity forecast</h2>
                <span className="text-theme-xs text-ink-dim">
                  {result.n_entities_forecast} entities
                  {(result.n_entities_skipped as number) > 0 && `, ${result.n_entities_skipped} skipped`}
                </span>
              </div>
              <ForecastExplorer
                predictions={result.predictions as any[]}
                historyByEntity={result.history_by_entity as Record<string, { period: string; actual: number }[]> | undefined}
                grain={result.grain as string}
              />
            </section>
          )}
          {goalType === 'forecast' && result?.forecast_type !== 'multi_entity' && result?.predictions && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-4">Forecast</h2>
              <SingleForecastDetail
                history={result.history as any[]}
                predictions={result.predictions as any[]}
                grain={result.grain as string}
              />
            </section>
          )}
          {goalType === 'anomaly' && result?.anomalies && (
            <section>
              <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">
                Anomalies <span className="text-ink-dim font-normal">({result.metrics?.n_anomalies} flagged)</span>
              </h2>
              <AnomalyTable
                anomalies={(result.anomalies as any[]).filter((a: any) => a.is_anomaly).slice(0, 50)}
                contributions={result.feature_contributions as any[]}
              />
            </section>
          )}
        </div>

        {/* ── Pre-analysis sidebar ──────────────────────────────────────────── */}
        <div>
          <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">Pre-analysis</h2>
          {!run.hypotheses || run.hypotheses.length === 0 ? (
            <p className="text-theme-xs text-ink-dim">No hypotheses computed yet.</p>
          ) : (
            <div className="space-y-2">
              {run.hypotheses.map((h, i) => <HypothesisCard key={i} h={h} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Pipeline stage stepper ────────────────────────────────────────────────────

function PipelineStages({
  stages,
}: {
  stages: Record<StageKey, StageStatus>
}) {
  return (
    <div className="border border-paper-border rounded-xl px-6 py-4 bg-white">
      <div className="flex items-center">
        {PIPELINE_STAGES.map((s, i) => {
          const status = stages[s.key]
          return (
            <div key={s.key} className="flex items-center flex-1 min-w-0">
              {/* Stage node */}
              <div className="flex flex-col items-center gap-1.5 w-20 shrink-0">
                <StageIcon status={status} />
                <span className={`text-[10px] font-semibold uppercase tracking-wider text-center leading-tight ${
                  status === 'active'
                    ? 'text-accent'
                    : status === 'done' || status === 'applied' || status === 'available'
                    ? 'text-ink'
                    : status === 'error'
                    ? 'text-red-600'
                    : 'text-ink-dim'
                }`}>
                  {s.label}
                </span>
                <span className={`text-[9px] text-center leading-tight ${
                  status === 'active' ? 'text-accent' : 'text-ink-dim'
                }`}>
                  {status === 'active' ? s.desc
                   : status === 'available' ? 'Ready to apply'
                   : status === 'applied' ? 'Applied'
                   : status === 'skipped' ? 'N/A'
                   : status === 'error' ? 'Failed'
                   : status === 'done' ? 'Done'
                   : '—'}
                </span>
              </div>

              {/* Connector line */}
              {i < PIPELINE_STAGES.length - 1 && (
                <div className={`flex-1 h-px mx-2 ${
                  stages[PIPELINE_STAGES[i + 1].key] !== 'pending'
                    ? 'bg-paper-border'
                    : 'bg-paper-border/50'
                }`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StageIcon({ status }: { status: StageStatus }) {
  const base = 'w-7 h-7 rounded-full flex items-center justify-center shrink-0'
  switch (status) {
    case 'active':
      return (
        <div className={`${base} bg-accent-soft border-2 border-accent/50`}>
          <Loader2 size={13} className="text-accent animate-spin" />
        </div>
      )
    case 'done':
      return (
        <div className={`${base} bg-emerald-100 border-2 border-emerald-400`}>
          <CheckCircle size={14} className="text-emerald-600" />
        </div>
      )
    case 'available':
      return (
        <div className={`${base} bg-accent-soft border-2 border-accent/50`}>
          <CheckCircle size={14} className="text-accent" />
        </div>
      )
    case 'applied':
      return (
        <div className={`${base} bg-emerald-100 border-2 border-emerald-500`}>
          <CheckCircle size={14} className="text-emerald-700" />
        </div>
      )
    case 'error':
      return (
        <div className={`${base} bg-red-100 border-2 border-red-400`}>
          <AlertCircle size={13} className="text-red-500" />
        </div>
      )
    case 'skipped':
      return (
        <div className={`${base} bg-paper-sunken border-2 border-paper-border`}>
          <Ban size={12} className="text-ink-dim" />
        </div>
      )
    default: // pending
      return (
        <div className={`${base} bg-paper-sunken border-2 border-paper-border`}>
          <Circle size={8} className="text-ink-dim" />
        </div>
      )
  }
}

// ── Hypothesis card ───────────────────────────────────────────────────────────

function HypothesisCard({ h }: { h: LensHypothesis }) {
  const label = HYP_LABELS[h.type] ?? h.type
  return (
    <div className="border border-paper-border rounded p-2.5 text-theme-xs space-y-0.5">
      <span className="text-[10px] font-semibold text-ink-dim uppercase tracking-wide">{label}</span>
      {h.type === 'correlation' && (
        <p className="text-ink"><strong>{h.field_a as string}</strong> ↔ <strong>{h.field_b as string}</strong>{' '}
          <span className={STRENGTH_COLOR[(h.strength as string) ?? '']}>{h.strength as string} ({h.metric as string} = {(h.value as number).toFixed(3)})</span>
        </p>
      )}
      {h.type === 'null_impact' && (
        <p className="text-ink">Missing <strong>{h.field as string}</strong>: {((h.without_parent_rate as number) * 100).toFixed(0)}% vs {((h.with_parent_rate as number) * 100).toFixed(0)}% rate</p>
      )}
      {h.type === 'distribution' && (
        <p className="text-ink"><strong>{h.field as string}</strong>: median {h.median as number}, {h.skew_label as string}</p>
      )}
      {h.type === 'null_rate' && (
        <p className="text-ink"><strong>{h.field as string}</strong>: {((h.null_pct as number) * 100).toFixed(0)}% missing{' '}
          <span className={STRENGTH_COLOR[(h.severity as string) ?? '']}>[{h.severity as string}]</span>
        </p>
      )}
      {h.type === 'cardinality' && (
        <p className="text-ink"><strong>{h.field as string}</strong>: {h.n_unique as number} unique, top "{h.top_value as string}" ({((h.top_pct as number) * 100).toFixed(0)}%)</p>
      )}
      {h.type === 'association' && (
        <p className="text-ink"><strong>{h.field_a as string}</strong> ↔ <strong>{h.field_b as string}</strong>{' '}
          <span className={STRENGTH_COLOR[(h.strength as string) ?? '']}>{h.strength as string}</span>
        </p>
      )}
    </div>
  )
}

// ── Result tables (unchanged) ─────────────────────────────────────────────────

function ScoresTable({ scores }: { scores: { coffee_id: string; predicted_class: string; probability: number }[] }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead><tr>
          <th>Record ID</th>
          <th>Predicted</th>
          <th className="text-right">Confidence</th>
        </tr></thead>
        <tbody>
          {scores.map((s) => (
            <tr key={s.coffee_id}>
              <td className="font-mono text-ink-dim truncate max-w-[140px]">{s.coffee_id.slice(0, 8)}…</td>
              <td className="font-medium">{s.predicted_class}</td>
              <td className="text-right">
                <span className={`font-medium ${s.probability > 0.75 ? 'text-emerald-600' : s.probability > 0.5 ? 'text-amber-600' : 'text-red-500'}`}>
                  {(s.probability * 100).toFixed(1)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SegmentsTable({ segments, featureNames }: { segments: any[]; featureNames: string[] }) {
  const topFeatures = (featureNames ?? []).slice(0, 4)
  return (
    <div className="space-y-2">
      {segments.map((s) => (
        <div key={s.cluster_id} className="border border-paper-border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-theme-xs font-semibold text-ink">Segment {s.cluster_id}</span>
            <span className="text-theme-xs text-ink-dim">{s.size.toLocaleString()} records</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {topFeatures.map((f) => (
              <div key={f} className="flex items-center justify-between text-theme-xs">
                <span className="text-ink-dim truncate">{f}</span>
                <span className="text-ink font-medium ml-2">{s.centroid?.[f]?.toFixed(2) ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function RulesTable({ rules }: { rules: { antecedent: string[]; consequent: string[]; support: number; confidence: number; lift: number }[] }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead><tr>
          <th>If…</th>
          <th>Then…</th>
          <th className="text-right">Support</th>
          <th className="text-right">Confidence</th>
          <th className="text-right">Lift</th>
        </tr></thead>
        <tbody>
          {rules.map((r, i) => (
            <tr key={i}>
              <td>{r.antecedent.join(', ')}</td>
              <td className="font-medium">{r.consequent.join(', ')}</td>
              <td className="text-right text-ink-dim">{(r.support * 100).toFixed(1)}%</td>
              <td className="text-right text-ink-dim">{(r.confidence * 100).toFixed(0)}%</td>
              <td className="text-right">
                <span className={r.lift > 2 ? 'text-emerald-600 font-medium' : r.lift > 1.2 ? 'text-amber-600' : 'text-ink-dim'}>{r.lift.toFixed(2)}×</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const _MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function _fmtPeriod(dateStr: string, grain: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const yr = d.getFullYear()
  const mo = _MONTHS[d.getMonth()]
  if (grain === 'month')   return `${mo} ${String(yr).slice(2)}`
  if (grain === 'quarter') return `Q${Math.floor(d.getMonth() / 3) + 1} ${String(yr).slice(2)}`
  if (grain === 'week')    return `${mo} ${d.getDate()}`
  return `${mo} ${d.getDate()}`
}

// ── Mini sparkline ────────────────────────────────────────────────────────────

function MiniSparkline({ values, active }: { values: number[]; active: boolean }) {
  if (values.length < 2) return <div className="h-4" />
  const W = 100, H = 20
  const max = Math.max(...values)
  const min = Math.min(0, ...values)
  const rng = max - min || 1
  const xOf = (i: number) => (i / (values.length - 1)) * W
  const yOf = (v: number) => H - ((v - min) / rng) * H
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ')
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-4 overflow-visible" preserveAspectRatio="none">
      <path d={d} fill="none" stroke={active ? '#a5b4fc' : '#e4e7ec'} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  )
}

// ── Entity card ───────────────────────────────────────────────────────────────

function EntityCard({
  title, totalForecast, history, active, onSelect,
}: {
  title: string
  totalForecast: number
  history: { period: string; actual: number }[]
  active: boolean
  onSelect: () => void
}) {
  const sparkVals = history.slice(-10).map((h) => h.actual)
  const fmt = (n: number) => n >= 10000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString(undefined, { maximumFractionDigits: 0 })
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
        active
          ? 'border-accent bg-accent-soft shadow-sm'
          : 'border-paper-border hover:border-paper-border hover:bg-paper-sunken/60'
      }`}
    >
      <div className="flex items-start justify-between gap-1 mb-1.5">
        <p className={`text-theme-xs font-medium leading-tight truncate ${active ? 'text-accent' : 'text-ink'}`}>
          {title}
        </p>
        <span className={`text-theme-xs font-semibold shrink-0 tabular-nums ${active ? 'text-accent' : 'text-ink-dim'}`}>
          {fmt(totalForecast)}
        </span>
      </div>
      <MiniSparkline values={sparkVals} active={active} />
    </button>
  )
}

// ── SVG forecast chart ────────────────────────────────────────────────────────

type ChartPoint = { period: string; value: number; type: 'history' | 'forecast' }
type TooltipState = { x: number; y: number; period: string; value: number; type: 'history' | 'forecast' } | null

function ForecastChart({
  history, predictions, grain,
}: {
  history: { period: string; actual: number }[]
  predictions: { period: string; predicted: number }[]
  grain: string
}) {
  const [tooltip, setTooltip] = useState<TooltipState>(null)

  const all: ChartPoint[] = [
    ...history.map((h) => ({ period: h.period, value: h.actual, type: 'history' as const })),
    ...predictions.map((p) => ({ period: p.period, value: p.predicted, type: 'forecast' as const })),
  ]

  if (all.length === 0) return <p className="text-sm text-ink-dim py-12 text-center">No data to chart.</p>

  const W = 720, H = 260
  const PAD = { t: 18, r: 24, b: 48, l: 52 }
  const iW = W - PAD.l - PAD.r
  const iH = H - PAD.t - PAD.b
  const n  = all.length

  const maxV = Math.max(...all.map((p) => p.value))
  const minV = Math.min(0, ...all.map((p) => p.value))
  const rng  = maxV - minV || 1

  const xOf = (i: number) => PAD.l + (i / Math.max(n - 1, 1)) * iW
  const yOf = (v: number) => PAD.t + (1 - (v - minV) / rng) * iH

  const hLen = history.length

  // Paths
  const histPath = history.map((h, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(h.actual).toFixed(1)}`).join(' ')
  const fcstPath = predictions.map((p, i) =>
    `${i === 0 ? 'M' : 'L'}${xOf(hLen + i).toFixed(1)},${yOf(p.predicted).toFixed(1)}`
  ).join(' ')
  const bridgePath = hLen > 0 && predictions.length > 0
    ? `M${xOf(hLen - 1).toFixed(1)},${yOf(history[hLen - 1].actual).toFixed(1)} L${xOf(hLen).toFixed(1)},${yOf(predictions[0].predicted).toFixed(1)}`
    : ''

  // Y ticks
  const yTicks = Array.from({ length: 5 }, (_, i) => minV + (i / 4) * rng)

  // X labels: show ~7 evenly spaced
  const labelSet = new Set<number>()
  const step = Math.max(1, Math.floor(n / 7))
  for (let i = 0; i < n; i += step) labelSet.add(i)
  labelSet.add(n - 1)

  const divX = hLen > 0 && hLen < n ? (xOf(hLen - 1) + xOf(hLen)) / 2 : null

  const fmtV = (v: number) => v >= 10000 ? `${(v / 1000).toFixed(1)}k` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full overflow-visible">
        {/* Y gridlines + labels */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.l} y1={yOf(v)} x2={W - PAD.r} y2={yOf(v)} stroke="#f2f4f7" strokeWidth={1} />
            <text x={PAD.l - 6} y={yOf(v) + 4} textAnchor="end" fontSize={10} fill="#98a2b3">{fmtV(v)}</text>
          </g>
        ))}

        {/* Forecast zone background */}
        {divX !== null && (
          <rect x={divX} y={PAD.t} width={W - PAD.r - divX} height={iH} fill="#eeecfd" opacity={0.55} />
        )}

        {/* Divider */}
        {divX !== null && (
          <line x1={divX} y1={PAD.t - 4} x2={divX} y2={PAD.t + iH + 6} stroke="#a5b4fc" strokeWidth={1.5} strokeDasharray="4,3" />
        )}

        {/* History line */}
        {histPath && <path d={histPath} fill="none" stroke="#98a2b3" strokeWidth={2} strokeLinejoin="round" />}

        {/* Bridge (dashed) */}
        {bridgePath && <path d={bridgePath} fill="none" stroke="#98a2b3" strokeWidth={1.5} strokeDasharray="4,3" />}

        {/* Forecast line */}
        {fcstPath && <path d={fcstPath} fill="none" stroke="#465fff" strokeWidth={2.5} strokeLinejoin="round" />}

        {/* History dots */}
        {history.map((h, i) => (
          <circle key={i} cx={xOf(i)} cy={yOf(h.actual)} r={4}
            fill="white" stroke="#98a2b3" strokeWidth={2} className="cursor-pointer"
            onMouseEnter={() => setTooltip({ x: xOf(i), y: yOf(h.actual), period: h.period, value: h.actual, type: 'history' })}
            onMouseLeave={() => setTooltip(null)}
          />
        ))}

        {/* Forecast dots */}
        {predictions.map((p, i) => (
          <circle key={i} cx={xOf(hLen + i)} cy={yOf(p.predicted)} r={5}
            fill="#465fff" stroke="white" strokeWidth={2} className="cursor-pointer"
            onMouseEnter={() => setTooltip({ x: xOf(hLen + i), y: yOf(p.predicted), period: p.period, value: p.predicted, type: 'forecast' })}
            onMouseLeave={() => setTooltip(null)}
          />
        ))}

        {/* X labels */}
        {all.map((pt, i) => labelSet.has(i) && (
          <text key={i} x={xOf(i)} y={H - PAD.b + 16} textAnchor="middle" fontSize={10}
            fill={pt.type === 'forecast' ? '#818cf8' : '#98a2b3'}>
            {_fmtPeriod(pt.period, grain)}
          </text>
        ))}

        {/* Tooltip */}
        {tooltip && (() => {
          const ttW = 108, ttH = 40
          const ttX = tooltip.x > W * 0.6 ? tooltip.x - ttW - 10 : tooltip.x + 10
          const ttY = tooltip.y < PAD.t + 44 ? tooltip.y + 6 : tooltip.y - ttH - 6
          return (
            <g>
              <rect x={ttX} y={ttY} width={ttW} height={ttH} rx={5} fill="white"
                stroke="#e4e7ec" strokeWidth={1}
                style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.10))' }}
              />
              <text x={ttX + 9} y={ttY + 14} fontSize={10} fill="#667085">{_fmtPeriod(tooltip.period, grain)}</text>
              <text x={ttX + 9} y={ttY + 29} fontSize={12} fontWeight="600"
                fill={tooltip.type === 'forecast' ? '#4338ca' : '#475467'}>
                {tooltip.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}
              </text>
            </g>
          )
        })()}

        {/* Legend */}
        <g transform={`translate(${PAD.l}, ${H - 8})`}>
          <line x1={0} y1={0} x2={14} y2={0} stroke="#98a2b3" strokeWidth={2} />
          <circle cx={7} cy={0} r={3} fill="white" stroke="#98a2b3" strokeWidth={2} />
          <text x={18} y={4} fontSize={10} fill="#98a2b3">History</text>
          <line x1={78} y1={0} x2={92} y2={0} stroke="#465fff" strokeWidth={2.5} />
          <circle cx={85} cy={0} r={4} fill="#465fff" />
          <text x={96} y={4} fontSize={10} fill="#465fff">Forecast</text>
        </g>
      </svg>
    </div>
  )
}

// ── Single-entity data table ───────────────────────────────────────────────────

function ForecastDataTable({
  history, predictions, grain,
}: {
  history: { period: string; actual: number }[]
  predictions: { period: string; predicted: number }[]
  grain: string
}) {
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 })
  const recentHist = history.slice(-18)
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th className="text-[10.5px] font-semibold">Period</th>
            <th className="text-right text-[10.5px] font-semibold">Value</th>
            <th className="text-[10.5px] font-semibold w-24">Type</th>
          </tr>
        </thead>
        <tbody>
          {recentHist.map((h) => (
            <tr key={h.period}>
              <td className="text-ink-dim">{_fmtPeriod(h.period, grain)}</td>
              <td className="text-right text-ink-dim tabular-nums">{fmt(h.actual)}</td>
              <td>
                <span className="text-[10px] text-ink-dim bg-paper-sunken px-1.5 py-0.5 rounded font-medium">History</span>
              </td>
            </tr>
          ))}
          {predictions.map((p) => (
            <tr key={p.period} className="bg-accent-soft/30">
              <td className="font-medium">{_fmtPeriod(p.period, grain)}</td>
              <td className="text-right font-semibold text-accent tabular-nums">{fmt(p.predicted)}</td>
              <td>
                <span className="text-[10px] text-accent bg-accent-soft px-1.5 py-0.5 rounded font-medium">Forecast</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Two-column forecast explorer (multi-entity) ───────────────────────────────

function ForecastExplorer({
  predictions, historyByEntity, grain,
}: {
  predictions: { entity_id: string; entity_title: string | null; period: string; predicted: number }[]
  historyByEntity?: Record<string, { period: string; actual: number }[]>
  grain: string
}) {
  const [search, setSearch]       = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [tab, setTab]             = useState<'chart' | 'data'>('chart')

  const entities = useMemo(() => {
    const map = new Map<string, { id: string; title: string; totalForecast: number }>()
    for (const p of predictions) {
      const e = map.get(p.entity_id)
      if (e) e.totalForecast += p.predicted
      else map.set(p.entity_id, { id: p.entity_id, title: p.entity_title || p.entity_id, totalForecast: p.predicted })
    }
    return [...map.values()].sort((a, b) => b.totalForecast - a.totalForecast)
  }, [predictions])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? entities.filter((e) => e.title.toLowerCase().includes(q)) : entities
  }, [entities, search])

  useEffect(() => {
    if (!selectedId && filtered.length > 0) setSelectedId(filtered[0].id)
  }, [filtered, selectedId])

  const selEntity     = entities.find((e) => e.id === selectedId)
  const selHistory    = historyByEntity?.[selectedId ?? ''] ?? []
  const selPredictions = predictions
    .filter((p) => p.entity_id === selectedId)
    .sort((a, b) => a.period.localeCompare(b.period))

  return (
    <div className="flex gap-4" style={{ minHeight: 500 }}>
      {/* Column A: entity list */}
      <div className="w-60 flex flex-col shrink-0 gap-2">
        <div className="relative">
          <input
            type="text"
            placeholder="Search entities…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="field w-full pl-7 text-theme-xs"
          />
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-dim pointer-events-none" />
        </div>
        <p className="text-[10.5px] text-ink-dim px-0.5">
          {filtered.length === entities.length ? `${entities.length} entities` : `${filtered.length} / ${entities.length}`}
        </p>
        <div className="flex-1 overflow-y-auto space-y-1 pr-0.5" style={{ maxHeight: 460 }}>
          {filtered.map((e) => (
            <EntityCard
              key={e.id}
              title={e.title}
              totalForecast={e.totalForecast}
              history={historyByEntity?.[e.id] ?? []}
              active={e.id === selectedId}
              onSelect={() => setSelectedId(e.id)}
            />
          ))}
          {filtered.length === 0 && (
            <p className="text-theme-xs text-ink-dim text-center py-8">No matches</p>
          )}
        </div>
      </div>

      {/* Column B: chart / data panel */}
      <div className="flex-1 min-w-0 border border-paper-border rounded-xl flex flex-col overflow-hidden">
        {/* Header with entity name + tabs */}
        <div className="flex items-center border-b border-paper-border px-4 gap-4 shrink-0">
          <p className="text-sm font-semibold text-ink flex-1 truncate py-2.5 min-w-0">
            {selEntity?.title ?? '—'}
          </p>
          <div className="flex shrink-0">
            {(['chart', 'data'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2.5 text-theme-xs font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
                }`}
              >
                {t === 'chart' ? 'Chart' : 'Data'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-auto p-4">
          {!selectedId ? (
            <p className="text-sm text-ink-dim text-center py-16">Select an entity from the list</p>
          ) : tab === 'chart' ? (
            <ForecastChart history={selHistory} predictions={selPredictions} grain={grain} />
          ) : (
            <ForecastDataTable history={selHistory} predictions={selPredictions} grain={grain} />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Single-series view with tabs ──────────────────────────────────────────────

function SingleForecastDetail({
  history, predictions, grain,
}: {
  history: { period: string; actual: number; predicted?: number }[]
  predictions: { period: string; predicted: number }[]
  grain: string
}) {
  const [tab, setTab] = useState<'chart' | 'data'>('chart')
  const histForChart = history.map((h) => ({ period: h.period, actual: h.actual }))

  return (
    <div className="border border-paper-border rounded-xl overflow-hidden">
      <div className="flex border-b border-paper-border px-4">
        {(['chart', 'data'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-theme-xs font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
            }`}
          >
            {t === 'chart' ? 'Chart' : 'Data'}
          </button>
        ))}
      </div>
      <div className="p-4">
        {tab === 'chart' ? (
          <ForecastChart history={histForChart} predictions={predictions} grain={grain} />
        ) : (
          <ForecastDataTable history={histForChart} predictions={predictions} grain={grain} />
        )}
      </div>
    </div>
  )
}

function AnomalyTable({ anomalies, contributions }: {
  anomalies: { coffee_id: string; score: number; is_anomaly: boolean }[]
  contributions: { field: string; delta: number; anomaly_mean: number; normal_mean: number }[]
}) {
  return (
    <div className="space-y-4">
      {contributions && contributions.length > 0 && (
        <div>
          <p className="text-theme-xs text-ink-dim mb-2">Top differentiating features:</p>
          <div className="space-y-1.5">
            {contributions.slice(0, 6).map((c) => (
              <div key={c.field} className="flex items-center gap-3 text-theme-xs">
                <span className="text-ink-dim w-36 truncate">{c.field}</span>
                <span className="text-ink-dim">normal: {c.normal_mean.toFixed(2)}</span>
                <span className="text-red-500">anomaly: {c.anomaly_mean.toFixed(2)}</span>
                <span className="text-ink-dim">Δ {c.delta.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr>
            <th>Record ID</th>
            <th className="text-right">Anomaly score</th>
          </tr></thead>
          <tbody>
            {anomalies.map((a) => (
              <tr key={a.coffee_id}>
                <td className="font-mono text-ink-dim">{a.coffee_id.slice(0, 8)}…</td>
                <td className="text-right"><span className="text-red-600 font-medium">{a.score.toFixed(4)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
